import { ExcludedFeature, FeatureMappingRow, UnmappedFeature } from '../data-qa-types';

type ChannelGroup = FeatureMappingRow['group'];

export interface FeatureSummaryRow {
  // The feature's name as the Sankey and the MMM Feature dropdown show it; '' for an
  // unmapped feature known only by its feature id.
  feature: string;
  featureId: string;
  // Declared in paid_media_exposure_vars but absent from the mapping data, so there is no
  // Sankey node or raw-component breakdown to jump to.
  unmapped: boolean;
}

const FEATID_PATTERN = /^featid\d+$/i;

// One group's features for the Feature Summary list: mapped features sorted by name (the
// MMM Feature dropdown's order), then unmapped ones. Grouping comes from each row's own
// `group` -- never the channel, since one channel (e.g. Paid Search) can span both groups.
export const buildFeatureSummaryRows = (
  group: ChannelGroup,
  featureMapping: FeatureMappingRow[],
  unmappedFeatures: UnmappedFeature[] = []
): FeatureSummaryRow[] => {
  const featureIds = new Map<string, Set<string>>();
  featureMapping
    .filter(row => row.group === group)
    .forEach(row => {
      const ids = featureIds.get(row.feature) ?? new Set<string>();
      row.featureId
        .split(',')
        .map(id => id.trim())
        .filter(Boolean)
        .forEach(id => ids.add(id));
      featureIds.set(row.feature, ids);
    });
  const mapped = [...featureIds]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([feature, ids]) => ({ feature, featureId: [...ids].join(', '), unmapped: false }));

  // One row per distinct unmapped feature: a featid shows as the id, anything else as the
  // name.
  const unmappedNames = new Set(
    unmappedFeatures.filter(u => u.group === group).map(u => u.feature)
  );
  const unmapped = [...unmappedNames].map(name =>
    FEATID_PATTERN.test(name)
      ? { feature: '', featureId: name.toUpperCase(), unmapped: true }
      : { feature: name, featureId: '', unmapped: true }
  );

  return [...mapped, ...unmapped];
};

// Data-frame features this model doesn't declare (config.excludedFeatures), for the Feature
// Summary's "Not used by this model" column -- sorted by name like the other columns.
export const buildNotInModelRows = (
  excludedFeatures: ExcludedFeature[] = []
): FeatureSummaryRow[] =>
  excludedFeatures
    .map(f => ({ feature: f.feature, featureId: f.featureId, unmapped: false }))
    .sort((a, b) => (a.feature || a.featureId).localeCompare(b.feature || b.featureId));
